import { useEffect, useRef, useState, type TransitionEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { leadsApi, feasibilityApi, ApiError } from "@/api";
import type {
  FeasibilitySummary,
  Lead,
  LeadDetail,
  LeadPatch,
  LeadStage,
  ConsultationOutcome,
  ProjectBrief,
} from "@/types";
import { LEAD_PIPELINE_STAGES, LEAD_OFFRAMP_STAGES } from "@/types";
import { Button, Spinner, StatusPill, TextField, TextAreaField, SelectField } from "@/components/ui";
import { effectiveStage, stageLabel, leadName, isSet } from "@/lib/leads";
import { formatDate, formatDateTime, formatRelative } from "@/lib/format";
import { isWebReportUrl, mergeBrief, missingRequired, normalizeBrief, prettyLabel,
         staffPreviewUrl } from "@/lib/feasibility";
import { lookupPostcodeAddresses, postcodeLookupAvailable, type PostcodeAddress } from "@/lib/postcode";
import { TemplatePickerModal } from "./TemplatePickerModal";
import { FeasibilityModal } from "./FeasibilityModal";
import { ReportEngagement } from "./ReportEngagement";
import { BriefFields } from "./BriefFields";

const ALL_STAGES: LeadStage[] = [...LEAD_PIPELINE_STAGES, ...LEAD_OFFRAMP_STAGES];

// Project details auto-save: persist shortly after the last edit so changes survive
// clicking away (a flush on close covers edits made inside this window).
const BRIEF_AUTOSAVE_MS = 800;
type BriefSaveStatus = "idle" | "saving" | "saved" | "error";
const OUTCOMES: { value: ConsultationOutcome; label: string }[] = [
  { value: "held", label: "Held" },
  { value: "no_show", label: "No-show" },
  { value: "cancelled", label: "Cancelled" },
  { value: "not_required", label: "Not required" },
];

export function LeadDrawer({
  lead,
  onClose,
  onChanged,
}: {
  lead: Lead;
  onClose: () => void;
  onChanged: (lead: Lead) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // drives the slide in/out transition
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [showPicker, setShowPicker] = useState(false);
  // "Description for quote": the quote's short description (project_description, the
  // {{short_description}} token). Deliberately NOT prefilled from the lead's project
  // description — that's the client's enquiry text; this must read as a noun phrase
  // that completes the quote's "covers…" sentence. Persisted on the lead as
  // `quote_description` (saved on blur, like the other fields) so a half-finished
  // drawer visit doesn't lose it; the create-quote call sends the draft directly and
  // #211 stamps it onto the lead too, so a blur-save still in flight can't race it.
  const [quoteDescription, setQuoteDescription] = useState("");
  const quoteDescriptionSaved = useRef(""); // last value loaded or sent — dedupes the blur PATCH
  const canCreateQuote = quoteDescription.trim() !== "";
  // Suggest: one engine call drafts the phrase from the enquiry text + brief; it lands in the
  // field (and saves) for staff to edit, so the wording stays theirs.
  const [suggesting, setSuggesting] = useState(false);
  const [showFeaso, setShowFeaso] = useState(false);
  const [outcome, setOutcome] = useState<ConsultationOutcome>("held");
  const [notes, setNotes] = useState("");
  const [recording, setRecording] = useState(false);
  // Project-details auto-save status (the section saves on its own, no Save button).
  const [briefStatus, setBriefStatus] = useState<BriefSaveStatus>("idle");

  // Slide in on mount (next frame so the initial off-screen transform paints).
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Animate out, then let the parent unmount once the slide finishes. Flush any pending
  // project-details edit first so closing the drawer never drops an unsaved change.
  function requestClose() {
    flushBrief();
    commitQuoteDescription();
    setOpen(false);
  }
  function handleTransitionEnd(e: TransitionEvent) {
    if (e.target === e.currentTarget && e.propertyName === "transform" && !open) onClose();
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    leadsApi
      .getLead(lead.id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setNotes("");
        setQuoteDescription(d.quote_description ?? "");
        quoteDescriptionSaved.current = (d.quote_description ?? "").trim();
        // Wake the engine now, whatever the lead's state: Suggest (no quote yet) and the
        // feasibility modal's /analyze loop (any lead) both run on the same web function, and
        // either would otherwise pay the cold start at the moment someone is waiting on it.
        feasibilityApi.warmEngine();
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the lead.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lead.id]);

  // Freshest detail for merge bases (avoids stale-closure clobbering of the
  // present-key `project_brief` write — two sections write it: §6 gotcha).
  const detailRef = useRef<LeadDetail | null>(null);
  useEffect(() => {
    detailRef.current = detail;
  }, [detail]);

  // Pending project-details auto-save: a dirty flag + debounce timer so we can both
  // persist after a pause and flush immediately when the drawer closes (else a quick
  // edit-then-click-away would be lost).
  const briefDirty = useRef(false);
  const briefTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function applyUpdate(updated: Lead) {
    setDetail((d) => (d ? { ...d, ...updated } : d));
    onChanged(updated);
  }

  async function savePatch(patch: LeadPatch) {
    try {
      const updated = await leadsApi.patchLead(lead.id, patch);
      applyUpdate(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
    }
  }

  /** Persist whatever brief is currently in `detail` (the freshest, already-merged
   *  value). Pins `project_brief` to exactly what we sent rather than trusting the
   *  PATCH response to echo the nested object back — the report modal reads it to prefill. */
  async function persistBrief() {
    const nextBrief = detailRef.current?.project_brief ?? {};
    setBriefStatus("saving");
    try {
      const updated = await leadsApi.patchLead(lead.id, { project_brief: nextBrief });
      const merged = { ...updated, project_brief: nextBrief } as Lead;
      setDetail((d) => (d ? { ...d, ...merged } : d));
      onChanged(merged);
      setBriefStatus("saved");
    } catch (err) {
      setBriefStatus("error");
      setError(err instanceof ApiError ? err.message : "Could not save project details.");
    }
  }

  /** Persist a pending edit right now (debounce hasn't fired yet), e.g. on close. */
  function flushBrief() {
    if (briefTimer.current) {
      clearTimeout(briefTimer.current);
      briefTimer.current = null;
    }
    if (!briefDirty.current) return;
    briefDirty.current = false;
    void persistBrief();
  }

  /** A Project-details edit: update `detail` optimistically (so Regenerate prefills from
   *  it immediately) and schedule a debounced auto-save so nothing relies on a Save click. */
  function updateBriefDraft(partial: Partial<ProjectBrief>) {
    setDetail((d) => (d ? { ...d, project_brief: mergeBrief(d.project_brief, partial) } : d));
    briefDirty.current = true;
    setBriefStatus("idle");
    if (briefTimer.current) clearTimeout(briefTimer.current);
    briefTimer.current = setTimeout(() => {
      briefTimer.current = null;
      if (!briefDirty.current) return;
      briefDirty.current = false;
      void persistBrief();
    }, BRIEF_AUTOSAVE_MS);
  }

  // Backstop: flush a pending edit if the drawer unmounts before the debounce fires.
  useEffect(() => () => flushBrief(), []);

  /** Persist the "Description for quote" draft if it changed (blur, close, Create quote). */
  function commitQuoteDescription() {
    const v = quoteDescription.trim();
    if (v === quoteDescriptionSaved.current) return;
    quoteDescriptionSaved.current = v;
    void savePatch({ quote_description: v });
  }

  /** Ask the engine for a draft "Description for quote" and put it in the field (saved like a
   *  typed value). Needs the enquiry text; the brief goes along when there is one. */
  async function suggestQuoteDescription() {
    const enquiry = detailRef.current?.project_description?.trim();
    if (!enquiry) return;
    setSuggesting(true);
    setError(undefined);
    try {
      const text = await feasibilityApi.suggestQuoteDescription({
        project_description: enquiry,
        project_brief: detailRef.current?.project_brief ?? null,
      });
      setQuoteDescription(text);
      quoteDescriptionSaved.current = text.trim();
      await savePatch({ quote_description: text.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not suggest a description.");
    } finally {
      setSuggesting(false);
    }
  }

  /** Postcode-lookup commit: the full structured site address + the richer brief
   *  resolution in one PATCH. Every column the pick resolves is written, so the lead's
   *  own fields — not just the brief blob — carry through to the quote via #211.
   *  Lines 2/3 are written even when empty, so re-picking a simpler address clears
   *  the parts left over from the previous one. */
  async function saveSiteFromLookup(hit: PostcodeAddress) {
    await savePatch({
      site_address_line_1: hit.line1,
      site_address_line_2: hit.line2,
      site_address_line_3: hit.line3,
      site_city: hit.postTown,
      site_county: hit.county,
      site_district: hit.council,
      site_postcode: hit.postcode,
      uprn: hit.uprn,
      lat: hit.latitude,
      lng: hit.longitude,
      project_brief: mergeBrief(detailRef.current?.project_brief, {
        address: hit.address,
        council: hit.council,
        uprn: hit.uprn,
        latitude: hit.latitude,
        longitude: hit.longitude,
      }),
    });
  }

  async function changeStage(stage: LeadStage) {
    if (!detail || stage === detail.stage) return;
    let reason = "";
    if (stage === "disqualified" || stage === "lost") {
      const r = window.prompt(`Reason for ${stageLabel(stage)}? (optional)`, detail.disqualified_reason ?? "");
      if (r === null) return;
      reason = r;
    }
    try {
      const updated = await leadsApi.moveStage(lead.id, stage, reason);
      applyUpdate(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not move the lead.");
    }
  }

  /** Spam is a flavour of disqualified (api 344): marking auto-moves the lead to
   *  disqualified and flags the WhatConverts lead as spam, so it drops out of the
   *  dashboard lead KPIs and WC reporting; unmarking clears the flag but leaves
   *  the lead disqualified. */
  async function toggleSpam(spam: boolean) {
    if (spam && !window.confirm(`Mark "${leadName(detail ?? lead)}" as spam? It moves to Disqualified and is excluded from lead KPIs and WhatConverts reporting.`)) return;
    try {
      const updated = await leadsApi.setLeadSpam(lead.id, spam);
      applyUpdate(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the spam flag.");
    }
  }

  async function submitConsultation() {
    setRecording(true);
    setError(undefined);
    try {
      const updated = await leadsApi.recordConsultation(lead.id, outcome, notes);
      applyUpdate(updated);
      setNotes("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not record the consultation.");
    } finally {
      setRecording(false);
    }
  }

  const d = detail;

  return (
    <div className={`lead-drawer__overlay${open ? " lead-drawer__overlay--open" : ""}`} onClick={requestClose}>
      <aside
        className={`lead-drawer${open ? " lead-drawer--open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={handleTransitionEnd}
        role="dialog"
        aria-label="Lead detail"
      >
        <header className="lead-drawer__head">
          <div>
            <h2>{leadName(d ?? lead)}</h2>
            {d ? <StatusPillRow stage={d.stage} spam={isSet(d.spam_at)} /> : null}
          </div>
          <button type="button" className="lead-drawer__close" onClick={requestClose} aria-label="Close">✕</button>
        </header>

        {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

        {loading || !d ? (
          <div className="lead-drawer__loading"><Spinner /></div>
        ) : (
          <div className="lead-drawer__body">
            {/* Stage */}
            <section className="lead-drawer__section">
              <SelectField label="Stage" value={effectiveStage(d.stage)} onChange={(e) => changeStage(e.target.value as LeadStage)}>
                {ALL_STAGES.map((s) => (
                  <option key={s} value={s}>{stageLabel(s)}</option>
                ))}
              </SelectField>
              {d.stage === "disqualified" && d.disqualified_reason ? (
                <p className="lead-drawer__muted">Reason: {d.disqualified_reason}</p>
              ) : null}
              {isSet(d.spam_at) ? (
                <p className="lead-drawer__muted">
                  <span className="lead-drawer__spam-tag">Spam</span> Excluded from lead KPIs and WhatConverts reporting.{" "}
                  <Button variant="ghost" size="sm" onClick={() => void toggleSpam(false)}>Not spam</Button>
                </p>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => void toggleSpam(true)}>Mark as spam</Button>
              )}
            </section>

            {/* Enquiry */}
            <section className="lead-drawer__section">
              <h3>Enquiry</h3>
              <EditableText
                label="Project description"
                value={d.project_description}
                multiline
                onSave={(v) => savePatch({ project_description: v })}
              />
              <dl className="lead-drawer__attr">
                {([
                  ["Source", d.source],
                  ["Medium", d.medium],
                  ["Campaign", d.campaign],
                  ["Keyword", d.keyword],
                ] as const)
                  .filter(([, v]) => !!v)
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
              </dl>
              <details className="lead-drawer__raw">
                <summary>Raw payload</summary>
                <pre>{JSON.stringify(d.raw_payload ?? {}, null, 2)}</pre>
              </details>
            </section>

            {/* Contact & site */}
            <section className="lead-drawer__section">
              <h3>Contact &amp; site</h3>
              <div className="lead-drawer__two">
                <EditableText label="First name" value={d.contact_first_name} onSave={(v) => savePatch({ contact_first_name: v })} />
                <EditableText label="Last name" value={d.contact_last_name} onSave={(v) => savePatch({ contact_last_name: v })} />
              </div>
              <EditableText label="Company" value={d.company_name} onSave={(v) => savePatch({ company_name: v })} />
              <label className="lead-drawer__check">
                <input
                  type="checkbox"
                  checked={!!d.is_company}
                  onChange={(e) => savePatch({ is_company: e.target.checked })}
                />
                Treat as company
              </label>
              <div className="lead-drawer__two">
                <EditableText label="Email" type="email" value={d.email} onSave={(v) => savePatch({ email: v })} />
                <EditableText label="Phone" value={d.phone} onSave={(v) => savePatch({ phone: v })} />
              </div>
              {/* Lookup first: the normal path is find-then-confirm, so the search leads and the
                  fields below it read as the result. Hand-editing them stays available. */}
              <SiteLookup
                postcode={d.site_postcode}
                onError={setError}
                onPick={(hit) => saveSiteFromLookup(hit)}
              />
              {/* Full structured address — labels mirror the Add-lead form. Editable so a
                  value captured at creation can be corrected before it reaches the quote (#211). */}
              <EditableText label="Site address line 1" value={d.site_address_line_1} onSave={(v) => savePatch({ site_address_line_1: v })} />
              <EditableText label="Site address line 2" value={d.site_address_line_2} onSave={(v) => savePatch({ site_address_line_2: v })} />
              <EditableText label="Site address line 3" value={d.site_address_line_3} onSave={(v) => savePatch({ site_address_line_3: v })} />
              <div className="lead-drawer__two">
                <EditableText label="Town / City" value={d.site_city} onSave={(v) => savePatch({ site_city: v })} />
                <EditableText label="County" value={d.site_county} onSave={(v) => savePatch({ site_county: v })} />
              </div>
              <div className="lead-drawer__two">
                <EditableText label="Local authority" value={d.site_district} onSave={(v) => savePatch({ site_district: v })} />
                <EditableText label="Postcode" value={d.site_postcode} onSave={(v) => savePatch({ site_postcode: v })} />
              </div>
              {d.project_brief?.address ? (
                <p className="lead-drawer__muted">
                  Resolved: {d.project_brief.address}
                  {d.project_brief.council ? ` · ${d.project_brief.council}` : ""}
                  {d.project_brief.uprn ? ` · UPRN ${d.project_brief.uprn}` : ""}
                </p>
              ) : null}
            </section>

            {/* Consultation */}
            <section className="lead-drawer__section">
              <h3>Consultation</h3>
              <p className="lead-drawer__muted">
                Booked: <span className="numeric">{formatDateTime(d.consultation_at)}</span>
                {d.consultation_outcome ? ` · ${d.consultation_outcome}` : ""}
              </p>
              {d.consultation_notes ? <p className="lead-drawer__notes">{d.consultation_notes}</p> : null}
              <div className="lead-drawer__consult-form">
                <SelectField label="Record outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as ConsultationOutcome)}>
                  {OUTCOMES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </SelectField>
                <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What came out of the call?" />
                <Button variant="secondary" size="sm" onClick={submitConsultation} loading={recording}>
                  Record consultation
                </Button>
              </div>
            </section>

            {/* Project details (feasibility inputs — captured here, not quote-gated) */}
            <ProjectDetails key={d.id} brief={d.project_brief} status={briefStatus} onChange={updateBriefDraft} />

            {/* Quote bridge */}
            <section className="lead-drawer__section">
              <h3>Quote</h3>
              {d.quote ? (
                <div className="lead-drawer__quote">
                  <div>
                    <span className="numeric">{d.quote.quote_number}</span>{" "}
                    <StatusPill status={d.quote.status} />
                  </div>
                  <Link to={`/staff/quotes/${d.quote.id}`}>
                    <Button variant="secondary" size="sm">View quote</Button>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="lead-drawer__quote-create">
                    <TextField
                      label="Description for quote"
                      value={quoteDescription}
                      onChange={(e) => setQuoteDescription(e.target.value)}
                      onBlur={commitQuoteDescription}
                      placeholder="e.g. a single-storey rear extension"
                    />
                    <div className="lead-drawer__quote-actions">
                      <Button
                        variant="secondary"
                        onClick={suggestQuoteDescription}
                        loading={suggesting}
                        disabled={!d.project_description?.trim()}
                        title="Draft this from the project description"
                      >
                        Suggest
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          commitQuoteDescription();
                          setShowPicker(true);
                        }}
                        disabled={!canCreateQuote}
                      >
                        Create quote
                      </Button>
                    </div>
                  </div>
                  <p className="lead-drawer__muted">
                    Completes the sentence on the quote saying what it covers — required before a quote can be created.
                    Suggest drafts it from the project description; edit freely.
                  </p>
                </>
              )}
            </section>

            {/* Feasibility */}
            <FeasibilitySection lead={d} onCreate={() => setShowFeaso(true)} />
          </div>
        )}
      </aside>

      {showPicker ? (
        <TemplatePickerModal
          onClose={() => setShowPicker(false)}
          onError={setError}
          leadId={lead.id}
          shortDescription={quoteDescription}
          onCreated={(quote) => {
            setShowPicker(false);
            navigate(`/staff/quotes/${quote.id}`);
          }}
          onAlreadyLinked={async () => {
            setShowPicker(false);
            // Re-read to pick up the existing quote link, then flip to "View quote".
            try {
              const fresh = await leadsApi.getLead(lead.id);
              setDetail(fresh);
              onChanged(fresh);
            } catch {
              /* leave as-is */
            }
          }}
        />
      ) : null}

      {showFeaso && d ? (
        <FeasibilityModal
          lead={d}
          quoteUrl={publishedQuoteUrl(d)}
          onClose={() => setShowFeaso(false)}
          onStored={(updated) => {
            setShowFeaso(false);
            applyUpdate(updated);
          }}
        />
      ) : null}
    </div>
  );
}

/** The published quote's public URL (spec §6 — the `quote_url` for /generate),
 *  or "" if there's no linked quote or it isn't published yet. Empty is a valid
 *  input: the report then renders its generic next-step copy instead of a quote CTA. */
function publishedQuoteUrl(d: LeadDetail): string {
  const slug = d.quote?.public_slug;
  return typeof slug === "string" && slug ? `${window.location.origin}/${slug}` : "";
}

/** When the report was generated, or null if the stored value isn't a usable timestamp.
 *  Reports written before API 213 was fixed carry the literal string "now" — a bare `now`
 *  in a XanoScript var expression evaluates as text — so those hide the line rather than
 *  render a broken date, and pick up a real one on their next regenerate. */
function reportGeneratedAt(summary: FeasibilitySummary | null | undefined): number | null {
  const raw = summary?.generated_at;
  if (raw === undefined || raw === null || raw === "") return null;
  const ms = typeof raw === "number" ? raw : Date.parse(String(raw));
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/** Copies the CLEAN report link — the one to send a client. Deliberately separate from "Open
 *  report", which is preview-tagged; see `staffPreviewUrl`. */
function CopyReportLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        navigator.clipboard.writeText(url).then(
          () => setCopied(true),
          () => setCopied(false),
        );
      }}
      title="Copy the client link (not the staff preview)"
    >
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

/** Feasibility section: shows the latest report at-a-glance with Open/Regenerate
 *  once one exists, else a Create button. Not gated on the quote — a report can be
 *  produced before one exists. */
function FeasibilitySection({ lead, onCreate }: { lead: LeadDetail; onCreate: () => void }) {
  const summary = lead.feasibility_summary;
  const hasReport = !!summary && !!lead.feasibility_report_url;
  // Answers "is this still current?" — the question the Regenerate button exists for.
  const generatedAt = reportGeneratedAt(summary);
  // The site-constraints lookup didn't complete on the last report → its "constraints" read as
  // unknown, and the PDF says so. Prompt staff to regenerate before sending.
  const constraintsUnchecked = hasReport && lead.constraints?.lookup_ok === false;

  return (
    <section className="lead-drawer__section">
      <h3>Feasibility</h3>
      {hasReport ? (
        <>
          {constraintsUnchecked ? (
            <p className="lead-drawer__warn" role="status">
              ⚠ Site constraints couldn’t be checked when this report was generated — regenerate before sending.
            </p>
          ) : null}
          <div className="feaso-result">
            {summary?.band ? (
              <span className={`feaso-band feaso-band--${String(summary.band).toLowerCase()}`}>{summary.band}</span>
            ) : null}
            <dl className="feaso-result__stats">
              {summary?.score_pct != null ? <div><dt>Score</dt><dd>{summary.score_pct}%</dd></div> : null}
              {summary?.approval_rate_pct != null ? <div><dt>Approval</dt><dd>{summary.approval_rate_pct}%</dd></div> : null}
              {summary?.case_study_count != null ? <div><dt>Cases</dt><dd>{summary.case_study_count}</dd></div> : null}
              {summary?.route ? <div><dt>Route</dt><dd>{summary.route}</dd></div> : null}
            </dl>
          </div>
          {generatedAt ? (
            <p className="lead-drawer__muted">
              Generated {formatRelative(generatedAt)} · {formatDate(generatedAt)}
            </p>
          ) : null}
          <div className="feaso-actions">
            {/* Opening is preview-tagged; copying is not. Two actions rather than one because a
                staff click must not stamp first_viewed_at, and the link that reaches the client
                must not carry ?preview=1 — with a single button, "copy link address" on it
                would send the preview URL and silently stop counting their real views. */}
            <a href={staffPreviewUrl(lead.feasibility_report_url!)} target="_blank" rel="noreferrer">
              <Button variant="secondary" size="sm">Open report</Button>
            </a>
            {isWebReportUrl(lead.feasibility_report_url) ? (
              <CopyReportLink url={lead.feasibility_report_url!} />
            ) : null}
            <Button variant="ghost" size="sm" onClick={onCreate}>
              Regenerate
            </Button>
          </div>
          {/* Only a web-view report has engagement to show — a legacy Xano PDF link predates the
              tracking entirely, and api 326 would return counters that are structurally zero. */}
          {isWebReportUrl(lead.feasibility_report_url) ? (
            <ReportEngagement leadId={lead.id} />
          ) : null}
        </>
      ) : (
        <Button variant="primary" onClick={onCreate}>
          Create report
        </Button>
      )}
    </section>
  );
}

function StatusPillRow({ stage, spam }: { stage: LeadStage; spam?: boolean }) {
  return (
    <span className="lead-drawer__stage-tag">
      {stageLabel(stage)}
      {spam ? <span className="lead-drawer__spam-tag">Spam</span> : null}
    </span>
  );
}

/** Postcode → address picker inside Contact & site (§5.3). Hidden when no
 *  Ideal Postcodes key is configured. The address dropdown exists only while there
 *  are results to choose from: it appears on a successful Find address, and clears
 *  again on pick or on any edit to the postcode (so it can never offer addresses
 *  belonging to a postcode that's no longer in the box). */
function SiteLookup({
  postcode,
  onError,
  onPick,
}: {
  postcode?: string;
  onError: (msg: string) => void;
  onPick: (hit: PostcodeAddress) => void | Promise<void>;
}) {
  const [query, setQuery] = useState(postcode ?? "");
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState<PostcodeAddress[]>([]);
  useEffect(() => setQuery(postcode ?? ""), [postcode]);

  if (!postcodeLookupAvailable) return null;

  async function search() {
    const q = query.trim();
    if (!q || busy) return;
    setBusy(true);
    setHits([]);
    try {
      const found = await lookupPostcodeAddresses(q);
      if (found.length === 0) {
        onError("No addresses found for that postcode — check it and try again.");
        return;
      }
      setHits(found);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Postcode lookup failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lead-drawer__lookup">
      <div className="lead-drawer__lookup-row">
        <input
          className="leads__search"
          placeholder="Look up a postcode"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value.toUpperCase());
            setHits([]); // results belong to the postcode that was searched, not the one being typed
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              search();
            }
          }}
        />
        <Button variant="secondary" size="sm" onClick={search} loading={busy} disabled={!query.trim()}>
          Find address
        </Button>
      </div>
      {hits.length > 0 ? (
        <SelectField
          label={`Select an address (${hits.length} found)`}
          value=""
          onChange={(e) => {
            if (e.target.value === "") return;
            const picked = hits[Number(e.target.value)];
            setHits([]); // collapse the picker — the pick now shows in the fields below
            onPick(picked);
          }}
        >
          <option value="">Choose…</option>
          {hits.map((h, i) => (
            <option key={h.uprn || i} value={i}>{h.label}</option>
          ))}
        </SelectField>
      ) : null}
    </div>
  );
}

/** Project-details capture (§5.3 #4): the feasibility inputs, saved into
 *  `project_brief`. Seeded once from the stored brief and not re-seeded, so an
 *  address-only lookup write elsewhere can't wipe in-progress edits here. */
function ProjectDetails({
  brief,
  status,
  onChange,
}: {
  brief: ProjectBrief | null | undefined;
  status: BriefSaveStatus;
  onChange: (partial: Partial<ProjectBrief>) => void;
}) {
  // Controlled by the parent's `detail.project_brief` (the single source of truth the
  // report modal also reads) — no private copy that could drift from what Regenerate sees.
  // Edits flow straight to the parent, which auto-saves; there is no Save button. The brief is
  // normalized to the property + extensions[] shape on read (migrating any pre-split storage).
  const norm = normalizeBrief(brief);
  const budgetBand = norm.budget_band ?? "";
  const property = norm.property ?? {};
  const extensions = norm.extensions ?? [{}];

  const missing = missingRequired(brief);
  const statusText =
    status === "saving" ? "Saving…" : status === "saved" ? "Saved" : status === "error" ? "Couldn’t save — try editing again." : "";

  return (
    <section className="lead-drawer__section">
      <h3>Project details</h3>
      <p className="lead-drawer__muted">Feasibility inputs — saved automatically and used to pre-fill the report.</p>
      <BriefFields
        budgetBand={budgetBand}
        property={property}
        extensions={extensions}
        onBudget={(v) => onChange({ budget_band: v })}
        onProperty={(patch) => onChange({ property: patch })}
        onExtensions={(next) => onChange({ extensions: next })}
      />
      {missing.length > 0 ? (
        <p className="lead-drawer__muted">Still needed for a report: {missing.map(prettyLabel).join(", ")}.</p>
      ) : null}
      <div className="lead-drawer__details-actions" aria-live="polite">
        {statusText ? <span className={`lead-drawer__saved${status === "error" ? " lead-drawer__saved--error" : ""}`}>{statusText}</span> : null}
      </div>
    </section>
  );
}

/** Inline text field that saves on blur only when the value changed. */
function EditableText({
  label,
  value,
  type = "text",
  multiline = false,
  onSave,
}: {
  label: string;
  value?: string;
  type?: string;
  multiline?: boolean;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  const commit = () => {
    if (draft !== (value ?? "")) onSave(draft);
  };
  if (multiline) {
    return <TextAreaField label={label} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />;
  }
  return <TextField label={label} type={type} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />;
}
